import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import * as Icons from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { SidebarEntryDto } from "@/types/api";
import { cn } from "@/design-system/lib/utils";
import { RecentlyViewedWidget } from "@/components/ux";
import { useAuth } from "@/hooks/useAuth";
import { useReunderwritingV1 } from "@/hooks/reunderwriting/useFeatureFlag";
import { useFeatureFlag } from "@/hooks/portfolioAdmin/useFeatureFlag";
import { useSidebarGroups } from "./SidebarGroupsContext";

interface SidebarProps {
  entries: SidebarEntryDto[];
  loading: boolean;
  mobileOpen: boolean;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

const GROUP_ORDER = [
  "Home",
  // Lead Generation System — the internal management section sits immediately
  // above PARTNERS (Prelead intake is upstream of the Leads pipeline).
  "Lead Generation",
  "Partners",
  // Buyer CRM (PR 2a) — the buy-side pipeline. Its own group rather than an
  // entry under Partners: a buyer is not a partner, and it sits between
  // Partners and Deal Flow because that is where it sits in the business — a
  // buyer becomes a deal.
  "Buyers",
  "Deal Flow",
  "Counterparties",
  "Reunderwriting",
  "Loan Administration",
  // Vendor Program v2 PR 2 — Vendors gets its own group (registry +
  // Complaints; PR 3 appends VFI Accounting). E1/E2/ADMIN reference
  // GROUP_ORDER directly and the E-VM/E5 vendor roles are unmapped (so
  // default-visible); mapped non-vendor roles deliberately don't see it.
  "Vendors",
  "Capital Markets",
  // Finance hosts the cross-loan money surfaces (Waterfalls, Loan Payments,
  // Loan Tape monthly close, Financial Reporting, VFI Accounting, Fees, Cost
  // Recovery, PIRs). The commission module is its own "Commissions" group
  // since the 2026-06 consolidation split them apart.
  "Finance",
  "Commissions",
  "Governance",
  "Tools",
  // Account-First Collection Consolidation (PRs #373-377) ships two
  // entries (Accounts, Review queue) under this group via SidebarMenu.cs;
  // FL-22 was the regression caused by this entry missing from
  // GROUP_ORDER, which silently dropped both items at render time.
  "External Collection",
  "Admin",
];

/**
 * Per-role visible groups. Roles not listed here default to "all groups visible".
 * If a user has multiple roles, the union of visible groups is shown.
 *
 * "Commissions" must appear for every A/B/C/D-tier role per the Commission
 * Module spec §1.3 — every team member can log claims against their own
 * revenue and so needs the Commissions sidebar item. "Finance" carries the
 * cross-loan money surfaces alongside it. The backend already filters
 * individual entries by permission (see ConfigController.Sidebar +
 * SidebarMenu.Entries), so adding a group to a role here only surfaces the
 * entries that role's permissions already grant.
 */
export const ROLE_GROUP_VISIBILITY: Record<string, string[]> = {
  ADMIN: GROUP_ORDER,
  E1: GROUP_ORDER,
  E2: GROUP_ORDER,
  // Full-Role-Capability Access Audit 2026-06 — the Internal Open Baseline +
  // External Collection bundle granted in that PR unlock these nav groups for
  // every in-scope role: Partners (Brokers/Brokerages), Deal Flow (Deals.Read
  // + Deal Staging), Counterparties, Loan Administration (Loans/Metrics),
  // Vendors (Vendors.Read), Tools (Documents/Reports/KB/Background Checks),
  // and External Collection. Each role keeps its prior groups (Reunderwriting,
  // Governance, Capital Markets) — these are unioned in, never removed. Do not
  // grant a permission while leaving its group hidden. The two Buyer CRM roles
  // at the bottom of this map are the recorded exception; see F2.
  //
  // Local Enforcers relocation (2026-06) — the entry moved into the Governance
  // group in SidebarMenu.cs. These A/C/D-tier roles hold Loans.Read (so they
  // saw it under Loan Administration) but none of the other Governance perms,
  // so adding "Governance" here surfaces ONLY Local Enforcers — Compliance /
  // Quality Assurance / Defaults & Workout stay permission-hidden.
  A1: ["Home", "Partners", "Buyers", "Deal Flow", "Counterparties", "Reunderwriting", "Loan Administration", "Vendors", "Finance", "Commissions", "Governance", "Tools", "External Collection"],
  A2: ["Home", "Partners", "Buyers", "Deal Flow", "Counterparties", "Reunderwriting", "Loan Administration", "Vendors", "Finance", "Commissions", "Governance", "Tools", "External Collection"],
  A3: ["Home", "Partners", "Buyers", "Deal Flow", "Counterparties", "Reunderwriting", "Loan Administration", "Vendors", "Finance", "Commissions", "Governance", "Tools", "External Collection"],
  A4: ["Home", "Partners", "Buyers", "Deal Flow", "Counterparties", "Reunderwriting", "Loan Administration", "Vendors", "Finance", "Commissions", "Governance", "Tools", "External Collection"],
  B1: ["Home", "Partners", "Buyers", "Deal Flow", "Counterparties", "Reunderwriting", "Loan Administration", "Vendors", "Finance", "Commissions", "Governance", "Tools", "External Collection"],
  B2: ["Home", "Partners", "Buyers", "Deal Flow", "Counterparties", "Reunderwriting", "Loan Administration", "Vendors", "Finance", "Commissions", "Governance", "Tools", "External Collection"],
  B3: ["Home", "Partners", "Buyers", "Deal Flow", "Counterparties", "Loan Administration", "Vendors", "Finance", "Commissions", "Governance", "Tools", "External Collection"],
  B4: ["Home", "Partners", "Buyers", "Deal Flow", "Counterparties", "Loan Administration", "Vendors", "Finance", "Commissions", "Governance", "Tools", "External Collection"],
  B5: ["Home", "Partners", "Buyers", "Deal Flow", "Counterparties", "Loan Administration", "Vendors", "Finance", "Commissions", "Governance", "Tools", "External Collection"],
  // C0 Broker Manager / Exec Director is the Lead-Gen Manager — the only mapped
  // role that holds LeadGen.* perms, so the only one that needs the group here
  // (ADMIN/E1/E2 get it via GROUP_ORDER; other roles lack the perms so the
  // permission-filtered group renders no entries for them).
  C0: ["Home", "Lead Generation", "Partners", "Buyers", "Deal Flow", "Counterparties", "Loan Administration", "Vendors", "Finance", "Commissions", "Governance", "Tools", "External Collection"],
  C1: ["Home", "Partners", "Buyers", "Deal Flow", "Counterparties", "Loan Administration", "Vendors", "Finance", "Commissions", "Governance", "Tools", "External Collection"],
  C2: ["Home", "Partners", "Buyers", "Deal Flow", "Counterparties", "Loan Administration", "Vendors", "Finance", "Commissions", "Governance", "Tools", "External Collection"],
  C3: ["Home", "Partners", "Buyers", "Deal Flow", "Counterparties", "Loan Administration", "Vendors", "Finance", "Commissions", "Governance", "Tools", "External Collection"],
  C4: ["Home", "Partners", "Buyers", "Deal Flow", "Counterparties", "Loan Administration", "Vendors", "Finance", "Commissions", "Governance", "Tools", "External Collection"],
  C5: ["Home", "Partners", "Buyers", "Deal Flow", "Counterparties", "Loan Administration", "Vendors", "Finance", "Commissions", "Governance", "Tools", "External Collection"],
  D1: ["Home", "Partners", "Buyers", "Deal Flow", "Counterparties", "Loan Administration", "Vendors", "Capital Markets", "Finance", "Commissions", "Governance", "Tools", "External Collection"],
  // Buyer CRM (PR 2a). Mapped rather than left to the unmapped default of
  // "every group visible": these are new roles, and a Buyer Consultant landing
  // on a sidebar of Capital Markets and Governance headers is noise, not
  // access. Counterparties is here because a qualified buyer converts to a
  // Borrower party; Deal Flow because that conversion is what becomes a deal.
  // The two roles differ only inside the Buyers group, where the assignment
  // queue is permission-gated to the manager.
  //
  // 🔴 U4 — F1 GAINS "Partners", AND F2 DELIBERATELY DOES NOT. An F1 is paid on
  // her broker book as well as her buyers (U2b), and U4 grants her the writes
  // that book needs: Brokers.Create/Update/Qualify, the Leads.* set,
  // Territories.Read, CallLogs.*. Leaving the group hidden would hand her
  // permissions with no door — the exact thing the audit comment above this map
  // warns against. F2 keeps the opposite answer on purpose; see below.
  //
  // Five of the group's six entries resolve for her: Brokers and Teams on the
  // baseline's Brokers.Read, Leads on U4's Leads.Read, Brokerages on the
  // baseline's Brokerages.Read, Prequalification on the Prequalification.Read
  // her role bundle already carries. Partner Brands stays hidden because
  // Partners.ManageBrands is one of the 18 permissions U4 deliberately does not
  // grant — the group is a door, not a grant, and each entry still gates on its
  // own permission.
  F1: ["Home", "Partners", "Buyers", "Counterparties", "Deal Flow", "Commissions", "Tools"],
  // 🔴 F2 IS GRANTED-BUT-HIDDEN IN SIX GROUPS, AND THAT IS A DECISION. The
  // Internal Open Baseline and the External Collection bundle reach every
  // non-ADMIN role, so the server would serve F2 entries in Partners (Brokers,
  // Teams, Brokerages and Prequalification — not Leads or Partner Brands), Loan
  // Administration, Vendors, Governance, Finance and External Collection. F1
  // is in the same position in all of those but Partners. The reads exist so a
  // Buyer Consultant can follow a link from a record they work; their sidebar
  // is their buyers. Decided 2026-09 (the Q6 left open by #1259) and pinned as
  // an exact lineup by UAT 04.05 in Sidebar.test.tsx. Showing any of those
  // groups is a product change: add it here.
  F2: ["Home", "Buyers", "Counterparties", "Deal Flow", "Commissions", "Tools"],
};

/**
 * SPV Pool v1.0 PR 4 — sidebar entries are sourced from the backend
 * ConfigController.Sidebar endpoint, so a new frontend-only page cannot
 * be surfaced purely client-side. These static fallback entries are
 * merged in (deduped by route) so the Capital Markets pool + trust
 * workspaces are reachable from the nav even before the backend config
 * seed ships the entries. Once the backend serves them this merge is a
 * harmless no-op (the backend rows win the dedupe).
 *
 * 🔴 A MIRROR CARRIES ITS BACKEND ENTRY'S PERMISSION, AND IS SHOWN ONLY TO A
 * USER WHO HOLDS IT. The backend omits an entry the user may not open, and the
 * merge fills in every route the backend response is missing — so an ungated
 * mirror of a gated entry was re-added for exactly the people the backend had
 * hidden it from. A B1 saw Fee Review, Monthly Statements, Payment Reviews,
 * Card Surcharge and Commission Modeler Terms, and was turned away by each.
 * "The dedupe makes it a no-op" held only for a user the backend had served
 * the entry to. `Sidebar.test.tsx` holds each mirror's permission
 * equal to its SidebarMenu.cs entry's, read from the C#.
 */
export interface SidebarFallbackEntry extends SidebarEntryDto {
  /** The permission VALUE the backend entry requires; absent where the backend
   *  entry requires none, or where there is no backend entry to mirror. */
  permission?: string;
}

export const STATIC_FALLBACK_ENTRIES: SidebarFallbackEntry[] = [
  // Calendar Appointments v1.0 — mirror of the SidebarMenu.cs "calendar"
  // entry (Home group, open to all) so the native calendar is reachable
  // before the backend config redeploys; the route-dedupe makes this a
  // no-op once the backend serves it.
  {
    key: "calendar",
    label: "Calendar",
    route: "/calendar",
    icon: "CalendarDays",
    group: "Home",
  },
  // Mirror of the SidebarMenu.cs "prequalification" entry. This list existed
  // precisely so a nav item survives a stale backend config, and this was the
  // one entry missing from it — so the surface it points at could vanish from
  // the nav entirely, which is the failure the fallback exists to prevent.
  // Group is Partners, matching the backend; the route deliberately stays
  // /deal-flow/prequalification, since backend notification deep links target
  // it. Route-dedupe makes this a no-op once the backend serves the entry.
  {
    key: "prequalification",
    label: "Prequalification",
    route: "/deal-flow/prequalification",
    icon: "ClipboardCheck",
    group: "Partners",
    permission: "Prequalification.Read",
  },
  // Buyer CRM (PR 2a) — mirrors of the four SidebarMenu.cs "Buyers" entries, so
  // the module is reachable before the backend config redeploys. Route-dedupe
  // makes them a no-op once the backend serves them.
  //
  // The assignment queue is deliberately NOT mirrored here. When this was
  // written the fallback list carried no permission, so a mirror of that
  // Buyers.ManageAll surface would have put the manager's queue in every
  // specialist's sidebar. Mirrors carry their permission now; the queue is
  // still left to the backend. The other three are open to all staff by
  // design (L37).
  {
    key: "buyers",
    label: "Buyers",
    route: "/buyers",
    icon: "Users",
    group: "Buyers",
    permission: "Buyers.Read",
  },
  {
    key: "listings",
    label: "Listings",
    route: "/listings",
    icon: "Store",
    group: "Buyers",
    permission: "Listings.Read",
  },
  {
    key: "buyer-submissions",
    label: "Submissions",
    route: "/buyer-submissions",
    icon: "Send",
    group: "Buyers",
    permission: "Submissions.Read",
  },
  // Multi-manager teams (WS4). Buyers.Read-gated like the three above — open
  // to all staff by design. Advertising is deliberately NOT mirrored:
  // BuyerAdvertising.Read is held by F1/C0/ADMIN only, and it is left to the
  // backend, which omits it for everyone else.
  {
    key: "buyer-teams",
    label: "Teams",
    route: "/buyers/teams",
    icon: "Users2",
    group: "Buyers",
    permission: "Buyers.Read",
  },
  // WS-B4 — Buyers.Read-gated like Teams, so mirroring it shows nobody
  // anything new; the advertising tab inside it hides itself.
  {
    key: "buyer-reports",
    label: "Reports",
    route: "/buyers/reports",
    icon: "BarChart3",
    group: "Buyers",
    permission: "Buyers.Read",
  },
  {
    key: "capital-markets-dashboard",
    label: "Dashboard",
    route: "/capital-markets",
    icon: "LayoutDashboard",
    group: "Capital Markets",
  },
  {
    key: "spv-pools",
    label: "SPV Pools",
    route: "/capital-markets/pools",
    icon: "Layers",
    group: "Capital Markets",
  },
  {
    key: "spv-trusts",
    label: "SPV Trusts",
    route: "/capital-markets/trusts",
    icon: "Landmark",
    group: "Capital Markets",
  },
  {
    key: "spv-investors",
    label: "Investors",
    route: "/capital-markets/investors",
    icon: "Users",
    group: "Capital Markets",
  },
  {
    key: "spv-service-providers",
    label: "Service Providers",
    route: "/capital-markets/service-providers",
    icon: "Briefcase",
    group: "Capital Markets",
  },
  // Reunderwriting Module (Tier 3) PR 3 — pipeline entry. PR 4 adds the
  // Metrics dashboard, Failure Tracking surface, and Escalations surface.
  // Backend ConfigController.Sidebar will ship the canonical seed later;
  // this fallback keeps the nav reachable in the meantime.
  // Knowledge Base v1.0 PR 4 — Ask + Browse landing. Backend
  // SidebarMenu.cs already ships the canonical entry; this fallback keeps
  // the nav reachable in environments where the backend config hasn't
  // been redeployed (the route dedupe makes it a no-op once it has).
  {
    key: "knowledge-base",
    label: "Knowledge Base",
    route: "/knowledge-base",
    icon: "BookOpen",
    group: "Tools",
    permission: "KnowledgeBase.Ask",
  },
  // NOTE: "Collection Templates" + "Bundles" are intentionally NOT mirrored
  // here. They are served (permission-gated on Collection.ManageTemplates) by
  // SidebarMenu.cs; an ungated fallback would re-add them for users WITHOUT
  // that permission whenever the backend correctly omits them (the fallback
  // only fills in routes the backend response is missing). Letting the
  // backend be the sole source keeps them hidden unless the user can manage
  // templates/bundles.
  {
    key: "deal-submission-templates",
    label: "Deal Templates",
    route: "/admin/deal-submission-templates",
    icon: "ClipboardEdit",
    group: "External Collection",
    permission: "DealSubmission.ManageTemplates",
  },
  {
    key: "portal-materials",
    label: "Portal Materials",
    route: "/admin/portal-materials",
    icon: "Library",
    group: "External Collection",
    permission: "PortalMaterials.Manage",
  },
  // Broker Channel Batch (WS6) — mirror of the SidebarMenu.cs entry so the
  // Teams link is reachable before the backend config redeploys (the
  // route-dedupe makes this a no-op once the backend serves it).
  {
    key: "broker-teams",
    label: "Teams",
    route: "/brokers/teams",
    icon: "Users",
    group: "Partners",
    permission: "Brokers.Read",
  },
  {
    key: "document-request-templates",
    label: "Document Request Templates",
    route: "/admin/document-request-templates",
    icon: "FileCheck",
    group: "Admin",
    permission: "DocumentRequestTemplates.Manage",
  },
  // Portfolios — named loan groupings (registry + per-portfolio dashboard).
  // FE-static like the other Loan Administration extras; the route itself
  // is Loans.Read-gated.
  {
    key: "portfolios",
    label: "Portfolios",
    route: "/portfolio-admin/portfolios",
    icon: "FolderKanban",
    group: "Loan Administration",
  },
  // Income Overview — mirror of the SidebarMenu.cs Finance entry so the
  // whole-book income roll-up is reachable before the backend config
  // redeploys; the route-dedupe makes this a no-op once the backend
  // (permission-gated) entry lands.
  {
    key: "income-overview",
    label: "Income Overview",
    route: "/portfolio-admin/income-overview",
    icon: "PieChart",
    group: "Finance",
    permission: "Loans.Read",
  },
  // Transitions console — mirror of the SidebarMenu.cs Finance entry so the
  // cross-loan exit-path console is reachable before the backend config
  // redeploys; the route-dedupe makes this a no-op once the backend
  // (Loans.Read-gated) entry lands.
  {
    key: "transitions",
    label: "Transitions",
    route: "/portfolio-admin/transitions",
    icon: "ArrowRightLeft",
    group: "Finance",
    permission: "Loans.Read",
  },
  // Fee Review — mirror of the SidebarMenu.cs Finance entry so the B3
  // approve/waive queue for system-assessed fees is reachable before the
  // backend config redeploys. Shown only to a Fees.Review holder, as the
  // backend entry and the /portfolio-admin/fee-review route both are.
  {
    key: "fee-review",
    label: "Fee Review",
    route: "/portfolio-admin/fee-review",
    icon: "ClipboardCheck",
    group: "Finance",
    permission: "Fees.Review",
  },
  // Monthly Statements — mirror of the SidebarMenu.cs Finance entry so the
  // B3 statement review/correction queues are reachable before the backend
  // config redeploys. Shown only to a Statements.Generate holder, as the
  // backend entry and the page both are.
  {
    key: "monthly-statements",
    label: "Monthly Statements",
    route: "/portfolio-admin/statements",
    icon: "Receipt",
    group: "Finance",
    permission: "Statements.Generate",
  },
  // Distribution Gate (B3 payout gate) — mirror of the SidebarMenu.cs Finance
  // entry so the B3 servicing-run review queue is reachable before the backend
  // config redeploys. Shown only to a Payments.Review holder, as the backend
  // entry and the page both are.
  {
    key: "payment-reviews",
    label: "Payment Reviews",
    route: "/portfolio-admin/payment-reviews",
    icon: "ClipboardCheck",
    group: "Finance",
    permission: "Payments.Review",
  },
  // PR-F2 — mirror of the SidebarMenu.cs Card Surcharge entry so the per-state
  // policy grid is reachable before the backend config redeploys. Shown only
  // to a Portfolio.ManageTpsStripe holder, as the backend entry and the
  // /portfolio-admin/card-surcharge route both are.
  {
    key: "card-surcharge",
    label: "Card Surcharge",
    route: "/portfolio-admin/card-surcharge",
    icon: "CreditCard",
    group: "Finance",
    permission: "Portfolio.ManageTpsStripe",
  },
  // Mirror of the SidebarMenu.cs Commission Modeler Terms entry so the program-terms
  // editor is reachable before the backend config redeploys. Shown only to a
  // Partners.ManageBrands holder, as the backend entry and the
  // /portfolio-admin/commission-modeler-terms route both are.
  {
    key: "commission-modeler-terms",
    label: "Commission Modeler Terms",
    route: "/portfolio-admin/commission-modeler-terms",
    icon: "Calculator",
    group: "Finance",
    permission: "Partners.ManageBrands",
  },
  {
    key: "reunderwriting-pipeline",
    label: "Pipeline",
    route: "/reunderwriting/pipeline",
    icon: "RefreshCw",
    group: "Reunderwriting",
  },
  {
    key: "reunderwriting-metrics",
    label: "Metrics",
    route: "/reunderwriting/metrics",
    icon: "BarChart3",
    group: "Reunderwriting",
  },
  {
    key: "reunderwriting-failure-tracking",
    label: "Failure Tracking",
    route: "/reunderwriting/failure-tracking",
    icon: "AlertOctagon",
    group: "Reunderwriting",
  },
  {
    key: "reunderwriting-escalations",
    label: "Escalations",
    route: "/reunderwriting/escalations",
    icon: "ArrowUpCircle",
    group: "Reunderwriting",
  },
];

export function mergeFallbackEntries(
  entries: SidebarEntryDto[],
  reunderwritingOn: boolean,
  capitalMarketsOn: boolean,
  hasPermission: (permission: string) => boolean,
): SidebarEntryDto[] {
  // A group is kept only when its build-time flag is on. Capital Markets is
  // FE-static (no backend seed today), so this gate is what hides it; the
  // backend-entry filter keeps the gate correct if it is ever seeded.
  const groupOn = (group: string) =>
    (reunderwritingOn || group !== "Reunderwriting") &&
    (capitalMarketsOn || group !== "Capital Markets");
  const seenRoutes = new Set(entries.map((e) => e.route));
  const fallback = STATIC_FALLBACK_ENTRIES.filter(
    (e) => groupOn(e.group) && (e.permission === undefined || hasPermission(e.permission)),
  );
  const missing = fallback.filter((e) => !seenRoutes.has(e.route));
  const filteredEntries = entries.filter((e) => groupOn(e.group));
  return missing.length > 0 ? [...filteredEntries, ...missing] : filteredEntries;
}

// `md` is 768px in the Tailwind config — the breakpoint at which the sidebar
// switches from the off-canvas mobile drawer to the static desktop rail.
function useIsDesktop() {
  const query = "(min-width: 768px)";
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window === "undefined" ? true : window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isDesktop;
}

export function Sidebar({ entries, loading, mobileOpen, collapsed, onToggleCollapsed }: SidebarProps) {
  const { user, hasPermission } = useAuth();
  const location = useLocation();
  const { isExpanded, toggle, ensureExpanded } = useSidebarGroups();

  // Collapse is a desktop-only affordance (the toggle is hidden below md). The
  // mobile drawer always opens at full width, so the icon-only state must not
  // leak into it — otherwise rotating a tablet from the compact desktop nav to
  // portrait shows a label-less, icon-only hamburger menu. Gate it to the md
  // breakpoint reactively so a resize/rotation updates the rendering.
  const isDesktop = useIsDesktop();
  const isCollapsed = collapsed && isDesktop;

  const reunderwritingOn = useReunderwritingV1();
  const capitalMarketsOn = useFeatureFlag("CapitalMarkets");
  const mergedEntries = useMemo(
    () => mergeFallbackEntries(entries, reunderwritingOn, capitalMarketsOn, hasPermission),
    [entries, reunderwritingOn, capitalMarketsOn, hasPermission],
  );
  const grouped = useMemo(() => groupByHeading(mergedEntries), [mergedEntries]);

  const visibleGroups = useMemo(
    () => GROUP_ORDER.filter((g) => grouped[g]?.length && isGroupVisibleForUser(g, user?.roleCodes ?? [])),
    [grouped, user?.roleCodes]
  );

  const activeGroup = useMemo(
    () => findActiveGroup(mergedEntries, location.pathname),
    [mergedEntries, location.pathname]
  );

  const allRoutes = useMemo(
    () => mergedEntries.map((e) => e.route),
    [mergedEntries]
  );

  useEffect(() => {
    if (activeGroup) ensureExpanded(activeGroup);
  }, [activeGroup, ensureExpanded]);

  return (
    <aside
      className={cn(
        "fixed md:static inset-y-0 left-0 z-50 h-screen md:h-auto md:min-h-screen",
        "bg-onyx text-white-pure flex flex-col transition-all duration-normal ease-standard",
        isCollapsed ? "md:w-16" : "md:w-64",
        "w-72 md:translate-x-0",
        mobileOpen ? "translate-x-0" : "-translate-x-full"
      )}
    >
      <div
        className={cn(
          "flex items-center h-16 px-space-3 border-b border-border-dark justify-between",
          isCollapsed && "md:justify-center"
        )}
      >
        {isCollapsed ? (
          <button
            type="button"
            onClick={onToggleCollapsed}
            className="flex items-center justify-center"
            aria-label="Expand sidebar"
          >
            <img
              src="/assets/logo/phoenix-bird-on-black.png"
              alt="Phoenix Capital"
              width={32}
              height={32}
              className="h-8 w-8 shrink-0 object-contain"
            />
          </button>
        ) : (
          <>
            <img
              src="/assets/logo/phoenix-bird-on-black.png"
              alt="Phoenix Capital"
              width={40}
              height={40}
              className="h-10 w-10 shrink-0 object-contain"
            />
            <button
              type="button"
              onClick={onToggleCollapsed}
              className="hidden md:inline-flex p-space-1 text-gold-shadow hover:text-gold"
              aria-label="Collapse sidebar"
            >
              <Icons.ChevronsLeft size={18} strokeWidth={1.5} aria-hidden />
            </button>
          </>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto py-space-3">
        {loading ? (
          <div className="px-space-4 text-caption text-gold-shadow uppercase tracking-wide">Loading…</div>
        ) : (
          visibleGroups.map((group) => (
            <SidebarGroup
              key={group}
              group={group}
              items={grouped[group]}
              allRoutes={allRoutes}
              collapsed={isCollapsed}
              expanded={isExpanded(group)}
              onToggle={() => toggle(group)}
            />
          ))
        )}

        <div className="mt-space-4 pt-space-3 border-t border-border-dark">
          <RecentlyViewedWidget collapsed={isCollapsed} />
        </div>
      </nav>
    </aside>
  );
}

interface SidebarGroupProps {
  group: string;
  items: SidebarEntryDto[];
  allRoutes: string[];
  collapsed: boolean;
  expanded: boolean;
  onToggle: () => void;
}

function SidebarGroup({ group, items, allRoutes, collapsed, expanded, onToggle }: SidebarGroupProps) {
  if (collapsed) {
    // Icon-only mode: hide group header entirely and render items directly.
    return (
      <div className="mb-space-3">
        <ul className="space-y-[2px]">
          {items.map((entry) => (
            <SidebarItem key={entry.key} entry={entry} allRoutes={allRoutes} collapsed={collapsed} />
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="mb-space-3">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className={cn(
          "w-full flex items-center justify-between px-space-4 py-space-1",
          "text-[11px] font-display font-medium uppercase text-gold",
          "tracking-[1.5px] cursor-pointer",
          "transition-colors duration-fast ease-standard",
          "hover:bg-[rgba(201,169,97,0.08)]"
        )}
      >
        <span className="min-w-0 flex-1 truncate text-left">{group}</span>
        <Icons.ChevronRight
          size={14}
          strokeWidth={1.5}
          aria-hidden
          className={cn(
            "text-gold transition-transform duration-fast ease-standard",
            expanded && "rotate-90"
          )}
        />
      </button>
      <div
        className="grid overflow-hidden transition-[grid-template-rows] duration-200 ease-standard"
        style={{ gridTemplateRows: expanded ? "1fr" : "0fr" }}
      >
        <div className="min-h-0 overflow-hidden">
          <ul className="space-y-[2px] pt-space-1">
            {items.map((entry) => (
              <SidebarItem key={entry.key} entry={entry} allRoutes={allRoutes} collapsed={collapsed} />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function SidebarItem({
  entry,
  allRoutes,
  collapsed,
}: {
  entry: SidebarEntryDto;
  allRoutes: string[];
  collapsed: boolean;
}) {
  const Icon = resolveIcon(entry.icon);
  const { pathname } = useLocation();
  // Deal detail pages live at /deal-flow/deals/* but belong to the Deal
  // Pipeline entry (/deal-flow/pipeline), which no prefix match can
  // catch. Light up Pipeline for those routes.
  const forceActive =
    entry.route === "/deal-flow/pipeline" && pathname.startsWith("/deal-flow/deals");
  const active = forceActive || isEntryActive(entry.route, pathname, allRoutes);
  return (
    <li>
      <Link
        to={entry.route}
        aria-current={active ? "page" : undefined}
        className={cn(
          "flex items-center gap-space-3 h-10 px-space-4 text-small",
          "font-display font-semibold tracking-wide",
          "transition-colors duration-fast ease-standard",
          "text-white-pure/70 hover:text-white-pure hover:bg-white-pure/5",
          active && "bg-gold/15 text-gold border-l-2 border-gold pl-[calc(theme(spacing.space-4)_-_2px)]"
        )}
      >
        <Icon size={18} strokeWidth={1.5} className="shrink-0" aria-hidden />
        {!collapsed && <span className="truncate">{entry.label}</span>}
      </Link>
    </li>
  );
}

/**
 * Longest-match-wins active state: an entry is active when the pathname is
 * its route or a nested path under it, AND no other sidebar entry matches
 * more specifically. This is the fix for the parent/child double-active
 * bug (e.g. "/compliance" lighting up alongside a "/compliance/…" child) —
 * exactly one entry can be active for any pathname, and it generalizes:
 * adding a future nested entry can never double-light its parent. The
 * root entry ("/") only matches exactly.
 */
export function isEntryActive(
  route: string,
  pathname: string,
  allRoutes: string[],
): boolean {
  const matches = (r: string) =>
    r === "/" ? pathname === "/" : pathname === r || pathname.startsWith(r + "/");
  if (!matches(route)) return false;
  return !allRoutes.some(
    (r) => r !== route && r.length > route.length && matches(r),
  );
}

function resolveIcon(name: string): LucideIcon {
  // @ts-expect-error lucide-react's dynamic lookup is not typed by name.
  return (Icons[name] as LucideIcon) ?? Icons.Circle;
}

function groupByHeading(entries: SidebarEntryDto[]) {
  return entries.reduce<Record<string, SidebarEntryDto[]>>((acc, e) => {
    (acc[e.group] ??= []).push(e);
    return acc;
  }, {});
}

export function isGroupVisibleForUser(group: string, roleCodes: string[]): boolean {
  if (roleCodes.length === 0) return true;
  // Roles with no explicit mapping default to seeing every group, so a single
  // unmapped role is enough to grant visibility.
  const unmapped = roleCodes.some((code) => !(code in ROLE_GROUP_VISIBILITY));
  if (unmapped) return true;
  return roleCodes.some((code) => ROLE_GROUP_VISIBILITY[code].includes(group));
}

function findActiveGroup(entries: SidebarEntryDto[], pathname: string): string | null {
  // Deal detail pages (/deal-flow/deals/*) belong to the Deal Pipeline
  // entry, whose route is /deal-flow/pipeline — not a prefix of the deal
  // URL — so resolve them to the pipeline entry's group explicitly.
  if (pathname.startsWith("/deal-flow/deals")) {
    const pipeline = entries.find((e) => e.route === "/deal-flow/pipeline");
    if (pipeline) return pipeline.group;
  }
  // Longest-prefix match so nested routes light up their parent entry's group.
  const sorted = [...entries].sort((a, b) => b.route.length - a.route.length);
  for (const entry of sorted) {
    if (entry.route === "/") {
      if (pathname === "/") return entry.group;
      continue;
    }
    if (pathname === entry.route || pathname.startsWith(entry.route + "/")) {
      return entry.group;
    }
  }
  return null;
}
